/**
 * TreeStateManager — pure functions for optimistic local updates on the
 * flat adjacency map (`treeItems: Record<string, LayoutTreeItem>`).
 *
 * These functions are used by the `useLayoutTree` hook to apply mutations
 * locally before confirming with the server, enabling instant UI feedback.
 *
 * The tree is a flat adjacency map where every node (including "root") is a
 * `LayoutTreeItem`. Parent→child relationships are tracked via `children: string[]`.
 */

import { nanoid } from 'nanoid'
import { uniqueName } from '@/lib/unique-name'

// ─── Types ───

export interface LayoutTreeItem {
  id: string
  name: string
  isFolder: boolean
  children: string[]
}

export type TreeItems = Record<string, LayoutTreeItem>

export const ROOT_ID = 'root'
export const MAX_FOLDER_DEPTH = 2

// ─── ID generators ───

export function layoutId(): string {
  return `lay_${nanoid()}`
}

export function folderId(): string {
  return `fld_${nanoid()}`
}

// ─── Query helpers ───

/**
 * Flat list of leaf (non-folder) layouts in tree order. Used by callers that
 * need a "layouts list" view of the tree (dashboard tabs, import picker).
 */
export function flattenLayouts(items: TreeItems): { layoutId: string; name: string }[] {
  const out: { layoutId: string; name: string }[] = []
  const stack = [...(items[ROOT_ID]?.children ?? [])]
  while (stack.length > 0) {
    const id = stack.shift()!
    const node = items[id]
    if (!node) continue
    if (node.isFolder) {
      stack.unshift(...node.children)
    } else {
      out.push({ layoutId: id, name: node.name })
    }
  }
  return out
}

/** Build a child→parent lookup. */
export function buildParentMap(items: TreeItems): Record<string, string> {
  const map: Record<string, string> = {}
  for (const [parentId, node] of Object.entries(items)) {
    for (const childId of node.children) {
      map[childId] = parentId
    }
  }
  return map
}

/** Find the parent of a node. Returns undefined for root or orphans. */
export function findParent(items: TreeItems, nodeId: string): string | undefined {
  for (const [parentId, node] of Object.entries(items)) {
    if (node.children.includes(nodeId)) return parentId
  }
  return undefined
}

/** Collect all descendant IDs (not including the node itself). */
export function collectDescendants(items: TreeItems, nodeId: string): string[] {
  const result: string[] = []
  const stack = [...(items[nodeId]?.children ?? [])]
  while (stack.length > 0) {
    const id = stack.pop()!
    result.push(id)
    const node = items[id]
    if (node?.children) stack.push(...node.children)
  }
  return result
}

/** Get depth of a node from root (root = 0, root's children = 1, etc.). */
export function getDepth(items: TreeItems, nodeId: string): number {
  if (nodeId === ROOT_ID) return 0
  const parentMap = buildParentMap(items)
  let depth = 0
  let current = nodeId
  while (current && current !== ROOT_ID) {
    current = parentMap[current]
    depth++
  }
  return depth
}

/** Get the max depth of the subtree rooted at nodeId. */
export function maxSubtreeDepth(items: TreeItems, nodeId: string): number {
  const node = items[nodeId]
  if (!node || node.children.length === 0) return 0
  let max = 0
  for (const childId of node.children) {
    max = Math.max(max, 1 + maxSubtreeDepth(items, childId))
  }
  return max
}

/** Check if ancestorId is an ancestor of descendantId. */
export function isAncestorOf(items: TreeItems, ancestorId: string, descendantId: string): boolean {
  const descendants = collectDescendants(items, ancestorId)
  return descendants.includes(descendantId)
}

/**
 * Given a list of nodeIds, remove any that are descendants of another
 * node in the list (so we don't double-process).
 */
export function deduplicateAncestors(items: TreeItems, nodeIds: string[]): string[] {
  return nodeIds.filter((id) => {
    return !nodeIds.some((otherId) => otherId !== id && isAncestorOf(items, otherId, id))
  })
}

// ─── Mutation helpers ───

/** Deep clone treeItems. */
export function cloneTree(items: TreeItems): TreeItems {
  const result: TreeItems = {}
  for (const [id, node] of Object.entries(items)) {
    result[id] = { ...node, children: [...node.children] }
  }
  return result
}

/**
 * Create a new node and add it to a parent's children array.
 * Returns the created node's ID.
 */
/**
 * The largest trailing `(n)` treated as a generated suffix rather than as part
 * of the name. "Day Trader (2)" re-numbers to "Day Trader (3)"; "Q4 (2024)" is
 * a name somebody typed, and duplicating it gives "Q4 (2024) (2)".
 */
/**
 * `name`, or `name (n)` with the lowest free n, so no two children of the same parent share a
 * name. The numbering itself lives in `@/lib/unique-name`.
 */
export function uniqueSiblingName(items: TreeItems, parentId: string, name: string): string {
  const parent = items[parentId]
  if (!parent) return name
  const taken = parent.children.map((id) => items[id]?.name).filter((n) => n != null)
  return uniqueName(name, taken)
}

export function createNode(
  items: TreeItems,
  parentId: string,
  name: string,
  isFolder: boolean,
  index?: number,
): string {
  const parent = items[parentId]
  if (!parent) throw new Error(`Parent ${parentId} not found`)

  const id = isFolder ? folderId() : layoutId()
  items[id] = { id, name: uniqueSiblingName(items, parentId, name), isFolder, children: [] }

  const idx = index != null ? Math.min(index, parent.children.length) : parent.children.length
  parent.children.splice(idx, 0, id)

  return id
}

/**
 * Delete nodes from the tree. Folders are removed entirely (hard delete).
 * Layout nodes are removed from the tree but their IDs are returned so
 * the caller can handle soft-delete logic.
 *
 * Returns the set of layout IDs that were removed (for soft-delete tracking).
 */
export function deleteNodes(items: TreeItems, nodeIds: string[]): string[] {
  const deduped = deduplicateAncestors(items, nodeIds)
  const deletedLayoutIds: string[] = []

  for (const nodeId of deduped) {
    // Collect all descendants first
    const descendants = collectDescendants(items, nodeId)
    const allIds = [nodeId, ...descendants]

    // Track layout IDs for soft-delete
    for (const id of allIds) {
      if (items[id] && !items[id].isFolder) {
        deletedLayoutIds.push(id)
      }
    }

    // Remove from parent's children
    const parentId = findParent(items, nodeId)
    if (parentId && items[parentId]) {
      items[parentId].children = items[parentId].children.filter((c) => c !== nodeId)
    }

    // Remove all nodes from the map
    for (const id of allIds) {
      delete items[id]
    }
  }

  return deletedLayoutIds
}

/**
 * Move nodes to a new parent at a given index.
 * Validates: no circular move, max depth.
 */
export function moveNodes(
  items: TreeItems,
  nodeIds: string[],
  targetParentId: string,
  index: number,
): void {
  const targetParent = items[targetParentId]
  if (!targetParent) throw new Error(`Target parent ${targetParentId} not found`)
  if (!targetParent.isFolder) throw new Error('Target must be a folder')

  for (const nodeId of nodeIds) {
    // Circular check
    if (nodeId === targetParentId || isAncestorOf(items, nodeId, targetParentId)) {
      throw new Error(`Cannot move ${nodeId} into its own descendant`)
    }

    // Depth check
    const targetDepth = getDepth(items, targetParentId)
    const subtreeDepth = maxSubtreeDepth(items, nodeId)
    if (targetDepth + 1 + subtreeDepth > MAX_FOLDER_DEPTH) {
      throw new Error(`Move would exceed max folder depth of ${MAX_FOLDER_DEPTH}`)
    }
  }

  // Remove from old parents
  for (const nodeId of nodeIds) {
    const oldParent = findParent(items, nodeId)
    if (oldParent && items[oldParent]) {
      items[oldParent].children = items[oldParent].children.filter((c) => c !== nodeId)
    }
  }

  // Insert at target index
  const clampedIndex = Math.min(index, targetParent.children.length)
  targetParent.children.splice(clampedIndex, 0, ...nodeIds)
}

/** Rename a node. */
export function renameNode(items: TreeItems, nodeId: string, name: string): void {
  const node = items[nodeId]
  if (!node) throw new Error(`Node ${nodeId} not found`)
  node.name = name
}

/**
 * Deep-copy a subtree, generating new IDs for each node.
 * Returns a map from old IDs to new IDs, plus the new items to merge.
 */
export function duplicateSubtree(
  items: TreeItems,
  nodeId: string,
): { idMap: Record<string, string>; newItems: TreeItems } {
  const node = items[nodeId]
  if (!node) throw new Error(`Node ${nodeId} not found`)

  const idMap: Record<string, string> = {}
  const newItems: TreeItems = {}

  // Only the node being duplicated is renamed. Suffixing every descendant too
  // meant duplicating a folder renamed everything inside it.
  const parentId = findParent(items, nodeId)
  const copyName = parentId
    ? uniqueSiblingName(items, parentId, node.name)
    : uniqueName(node.name, [node.name])

  function copyNode(id: string): string {
    const src = items[id]
    if (!src) throw new Error(`Node ${id} not found during duplicate`)

    const newId = src.isFolder ? folderId() : layoutId()
    idMap[id] = newId

    const newChildren = src.children.map(copyNode)
    newItems[newId] = {
      id: newId,
      name: id === nodeId ? copyName : src.name,
      isFolder: src.isFolder,
      children: newChildren,
    }

    return newId
  }

  copyNode(nodeId)
  return { idMap, newItems }
}

/**
 * Pick the next active layout ID when the current active is deleted.
 * Returns the first layout in root's children (DFS), or null.
 */
export function pickNextActive(items: TreeItems, excludeIds?: string[]): string | null {
  const exclude = new Set(excludeIds ?? [])
  const stack = [...(items[ROOT_ID]?.children ?? [])]
  while (stack.length > 0) {
    const id = stack.shift()! // BFS order through children
    if (exclude.has(id)) continue
    const node = items[id]
    if (!node) continue
    if (!node.isFolder) return id
    stack.push(...node.children)
  }
  return null
}

/**
 * Denormalize the flat tree into nested data suitable for react-arborist.
 * Each node gets a `children` array of nested objects instead of IDs.
 */
export interface TreeNodeData {
  id: string
  name: string
  isFolder: boolean
  children: TreeNodeData[]
}

export function denormalize(items: TreeItems): TreeNodeData[] {
  function buildNode(id: string): TreeNodeData | null {
    const item = items[id]
    if (!item) return null
    return {
      id: item.id,
      name: item.name,
      isFolder: item.isFolder,
      children: item.children.map(buildNode).filter(Boolean) as TreeNodeData[],
    }
  }

  const root = items[ROOT_ID]
  if (!root) return []
  return root.children.map(buildNode).filter(Boolean) as TreeNodeData[]
}
